(() => {
  if (!window.limencyPillowPreviewSettings) return;

  // BonaPer swaps the theme gallery for its design preview (design view) and
  // back (photos view) with a two-button switch. This script replaces that
  // switch with the gallery's own thumbnail strip: the first thumbnail is the
  // customer's pillow, the rest are product photos. Before a photo is ready
  // the first thumbnail invites an upload instead.
  const READY_CLASS = 'bonaper-pillow-upload-first--ready';
  const DESIGN_CLASS = 'bonaper-pillow-design';
  const SIDE_CLASS = 'bonaper-pillow-thumbs-side';
  const HIDDEN_GALLERY = 'bonaper-gallery-hidden';
  const root = document.documentElement;
  let item, itemButton, editButton, changeButton, readyLabel, scheduled = false, wasDesign = false;

  const photoField = () => document.querySelector('.bonaper-field[data-bonaper-field="pet-photo"]');
  const gallery = () => document.querySelector('media-gallery');
  const thumbList = () => gallery()?.querySelector('[id^="GalleryThumbnails"] ul');
  const inDesign = () => !!gallery()?.classList.contains(HIDDEN_GALLERY);
  const photoThumb = () => photoField()?.querySelector('.bonaper-photo__thumb:not([hidden])');
  const photoReady = () => root.classList.contains(READY_CLASS) && !!photoThumb()?.getAttribute('src');
  // Same signal as BonaPer's "Personalize it" panel: mounted, options no
  // longer busy and the file input in place. Until then the upload thumbnail
  // shows a spinner and cannot be clicked.
  const appReady = () => !!document.querySelector('[data-bonaper-root][data-bonaper-mounted]')
    && !document.querySelector('[data-bonaper-root] .bonaper-options__body[aria-busy="true"]')
    && !!photoField()?.querySelector('input.bonaper-file');
  // Writing an unchanged attribute still queues a mutation record, which would
  // wake the observer below every frame.
  const setHidden = (element, hidden) => { if (element.hidden !== hidden) element.hidden = hidden; };
  const clickSwitch = (view) => document.querySelector(`[data-bonaper-view-${view}]`)?.click();

  function openPicker() {
    const field = photoField();
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.querySelector('input.bonaper-file')?.click();
  }

  function showDesign() {
    if (photoReady() && !inDesign()) clickSwitch('design');
  }

  function buildItem() {
    item = document.createElement('li');
    // slider__slide picks up the theme's thumbnail sizing. The slider itself
    // only counts [id^="Slide-"] items, so this one stays out of its paging.
    item.className = 'thumbnail-list__item slider__slide bonaper-pillow-thumb-item';
    itemButton = document.createElement('button');
    itemButton.type = 'button';
    itemButton.className = 'thumbnail global-media-settings global-media-settings--no-shadow bonaper-pillow-thumb';
    itemButton.addEventListener('click', () => (photoReady() ? showDesign() : openPicker()));
    item.append(itemButton);
  }

  function renderItem() {
    const ready = photoReady();
    const src = ready ? photoThumb().getAttribute('src') : '';
    const state = ready ? 'ready' : appReady() ? 'empty' : 'loading';
    if (itemButton.dataset.state !== state || itemButton.dataset.src !== src) {
      itemButton.dataset.state = state;
      itemButton.dataset.src = src;
      itemButton.replaceChildren();
      itemButton.disabled = state === 'loading';
      if (state === 'loading') itemButton.setAttribute('aria-busy', 'true');
      else itemButton.removeAttribute('aria-busy');
      if (state === 'loading') {
        const spinner = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-thumb__spinner' });
        spinner.setAttribute('aria-hidden', 'true');
        const label = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-thumb__label', textContent: 'Loading…' });
        itemButton.append(spinner, label);
        itemButton.setAttribute('aria-label', 'Photo upload is loading');
      } else if (ready) {
        const image = Object.assign(document.createElement('img'), { src, alt: '' });
        const badge = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-thumb__badge', textContent: '3D' });
        itemButton.append(image, badge);
        itemButton.setAttribute('aria-label', 'Show your pillow design');
      } else {
        const icon = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-thumb__icon', textContent: '+' });
        icon.setAttribute('aria-hidden', 'true');
        const label = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-thumb__label', textContent: 'Your pet here' });
        itemButton.append(icon, label);
        itemButton.setAttribute('aria-label', 'Upload your pet photo');
      }
    }
    const current = ready && inDesign();
    if (current !== itemButton.hasAttribute('aria-current')) {
      if (current) itemButton.setAttribute('aria-current', 'true');
      else itemButton.removeAttribute('aria-current');
    }
  }

  function syncStrip() {
    const list = thumbList();
    if (!list) return;
    if (!item) buildItem();
    if (list.firstElementChild !== item) list.prepend(item);
    renderItem();
    const design = photoReady() && inDesign();
    root.classList.toggle(DESIGN_CLASS, design);
    // Desktop stacks the thumbnails in a column beside the photo; keep that
    // column beside the pillow preview too.
    const side = design && getComputedStyle(list).flexDirection === 'column';
    root.classList.toggle(SIDE_CLASS, side);
    // The column sticks at the same offset BonaPer gives the preview.
    const top = side ? getComputedStyle(document.querySelector('[data-bonaper-preview]')).top : '';
    if (root.style.getPropertyValue('--bonaper-pillow-sticky-top') !== top) root.style.setProperty('--bonaper-pillow-sticky-top', top);
    // Only one thumbnail reads as current: the pillow while the design shows.
    // The theme scrolls the strip to its own first photo; bring the pillow
    // thumbnail back into view whenever the design takes over.
    if (design && !wasDesign) list.scrollTo({ left: 0, top: 0 });
    wasDesign = design;
    if (design) {
      list.querySelectorAll('.thumbnail[aria-current]:not(.bonaper-pillow-thumb)').forEach((button) => {
        button.removeAttribute('aria-current');
      });
    }
  }

  // Edit photo sits on the preview itself, where the customer is looking.
  function syncEditButton() {
    const frame = document.querySelector('[data-bonaper-preview] .bonaper-preview__frame');
    if (!frame) return;
    if (!editButton) {
      editButton = document.createElement('button');
      editButton.type = 'button';
      editButton.className = 'bonaper-pillow-edit';
      editButton.textContent = 'Edit photo';
      editButton.addEventListener('click', () => photoField()?.querySelector('.bonaper-photo__edit')?.click());
    }
    if (editButton.parentElement !== frame) frame.append(editButton);
    setHidden(editButton, !(photoReady() && inDesign()));
  }

  // Once the photo is ready the upload row collapses to one line:
  // thumbnail, "Photo ready", Edit photo and Change photo.
  function syncPhotoRow() {
    const actions = photoField()?.querySelector('.bonaper-photo__actions');
    if (!actions) return;
    if (!readyLabel) {
      readyLabel = Object.assign(document.createElement('span'), { className: 'bonaper-pillow-photo-ready', textContent: 'Photo ready' });
      changeButton = document.createElement('button');
      changeButton.type = 'button';
      changeButton.className = 'bonaper-btn bonaper-btn--tiny bonaper-pillow-photo-change';
      changeButton.textContent = 'Change photo';
      changeButton.addEventListener('click', openPicker);
    }
    const thumb = actions.querySelector('.bonaper-photo__thumb');
    if (thumb && thumb.nextElementSibling !== readyLabel) thumb.after(readyLabel);
    if (changeButton.parentElement !== actions) actions.append(changeButton);
    const ready = photoReady();
    setHidden(readyLabel, !ready);
    setHidden(changeButton, !ready);
  }

  function sync() {
    scheduled = false;
    syncStrip();
    syncEditButton();
    syncPhotoRow();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // A timer rather than requestAnimationFrame, which stops in hidden tabs.
    setTimeout(sync, 30);
  }

  // Leaving the design: a product photo thumbnail switches BonaPer to photos
  // first, then the gallery's own handler shows that photo.
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest?.('[id^="GalleryThumbnails"] [data-target] button');
    if (thumb && inDesign()) clickSwitch('photos');
  }, true);

  // Coming back: changing size or print sides returns to the customer's pillow.
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('variant-selects, variant-radios, [data-bonaper-field="print-sides"]')) showDesign();
  });
  window.addEventListener('bonaper:pillow-ready', () => { showDesign(); schedule(); });

  new MutationObserver(schedule).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'src', 'aria-busy', 'data-bonaper-mounted'],
  });
  sync();
})();
